import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Drawer, DrawerProps } from 'antd';
import styled from 'styled-components';

interface ResizableDrawerProps extends DrawerProps {
  minWidth?: number;
  maxWidth?: number;
  storageKey?: string;
  isDesktop?: boolean;
}

const ResizeHandle = styled.div<{ $isResizing: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  ${(props) =>
    props.$isResizing &&
    `
    background-color: #1890ff;
  `}

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 40px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 1px;
  }
`;

const DrawerWrapper = styled.div`
  .ant-drawer-content {
    overflow: visible;
  }
`;

const ResizableDrawer: React.FC<ResizableDrawerProps> = ({
  minWidth = 400,
  maxWidth = 1600,
  storageKey = 'drawer-width',
  isDesktop = true,
  width: propWidth,
  children,
  ...restProps
}) => {
  const [width, setWidth] = useState<number>(() => {
    if (!isDesktop) return (propWidth as number) || 800;

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsedWidth = parseInt(stored, 10);
      if (!isNaN(parsedWidth)) {
        return Math.max(minWidth, Math.min(maxWidth, parsedWidth));
      }
    }
    return (propWidth as number) || 800;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isDesktop) return;

      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [isDesktop, width]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !isDesktop) return;

      const diff = startXRef.current - e.clientX;
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, startWidthRef.current + diff)
      );
      setWidth(newWidth);
    },
    [isResizing, isDesktop, minWidth, maxWidth]
  );

  const handleMouseUp = useCallback(() => {
    if (!isResizing) return;

    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save to localStorage
    if (isDesktop && storageKey) {
      localStorage.setItem(storageKey, width.toString());
    }
  }, [isResizing, isDesktop, storageKey, width]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Update width when prop changes (for non-desktop or when not stored)
  useEffect(() => {
    if (!isDesktop && propWidth) {
      setWidth(propWidth as number);
    }
  }, [isDesktop, propWidth]);

  // Restore saved width when switching from mobile to desktop
  useEffect(() => {
    if (isDesktop && storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedWidth = parseInt(stored, 10);
        if (!isNaN(parsedWidth)) {
          setWidth(Math.max(minWidth, Math.min(maxWidth, parsedWidth)));
          return;
        }
      }
      // If no stored value, use default
      setWidth((propWidth as number) || 800);
    }
  }, [isDesktop, storageKey, minWidth, maxWidth, propWidth]);

  return (
    <DrawerWrapper>
      <Drawer {...restProps} width={width}>
        {isDesktop && restProps.open && (
          <ResizeHandle
            onMouseDown={handleMouseDown}
            $isResizing={isResizing}
          />
        )}
        {children}
      </Drawer>
    </DrawerWrapper>
  );
};

export default ResizableDrawer;
